import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetEventAttendanceQueryKey,
  getListRollCallsQueryKey,
  useGetEvent,
  useGetEventAttendance,
  useGetStudentSmsLogs,
  useUpdateAttendance,
  useUpdateEvent,
  useListRollCalls,
  useCreateRollCall,
  useUpdateRollCall,
  useDeleteRollCall,
  useGetRollCallEntries,
  getGetRollCallEntriesQueryKey,
  type AttendanceRecord as AttendanceT,
  type SmsLog,
  type CheckoutType,
  type RollCall,
  type RollCallEntry
} from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  CalendarDaysIcon,
  LogOutIcon,
  MessageSquareIcon,
  AlertTriangleIcon,
  CheckIcon,
  XIcon,
  SmartphoneIcon,
  UsersIcon,
  GraduationCapIcon,
  UserCheckIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ClipboardListIcon
} from 'lucide-react';
import { useSession } from '@/components/auth/user-provider';

/* ------------------------------ preference helpers ------------------------------ */

type PreferencePresentation = {
  label: string;
  helper: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeClassName: string;
  cardClassName: string;
  railClassName: string;
  iconClassName: string;
  panelClassName: string;
  confirmationClassName: string;
};

function getPreferencePresentation(preference: CheckoutType | null | undefined): PreferencePresentation {
  switch (preference) {
    case 'by_self':
      return {
        label: 'Go home independently',
        helper: 'Parent permission received for an independent departure.',
        icon: CheckIcon,
        badgeClassName: 'border-transparent bg-success text-success-foreground',
        cardClassName: 'border-success/35 bg-success/5 hover:border-success/65 hover:shadow-success/10',
        railClassName: 'bg-success',
        iconClassName: 'bg-success/15 text-success',
        panelClassName: 'border-success/25 bg-success/10',
        confirmationClassName: 'border-transparent bg-success text-success-foreground hover:bg-success/90'
      };
    case 'wait_for_parent':
      return {
        label: 'Wait for parent pickup',
        helper: 'Keep this student with staff until their parent or guardian is present.',
        icon: ClockIcon,
        badgeClassName: 'border-transparent bg-warning text-warning-foreground',
        cardClassName: 'border-warning/40 bg-warning/5 hover:border-warning/70 hover:shadow-warning/10',
        railClassName: 'bg-warning',
        iconClassName: 'bg-warning/20 text-warning-foreground',
        panelClassName: 'border-warning/30 bg-warning/10',
        confirmationClassName: 'border-transparent bg-warning text-warning-foreground hover:bg-warning/90'
      };
    case 'back_to_school':
      return {
        label: 'Return to school',
        helper: 'This student is travelling back to school with the supervising team.',
        icon: CalendarDaysIcon,
        badgeClassName: 'border-transparent bg-info text-info-foreground',
        cardClassName: 'border-info/35 bg-info/5 hover:border-info/65 hover:shadow-info/10',
        railClassName: 'bg-info',
        iconClassName: 'bg-info/15 text-info',
        panelClassName: 'border-info/25 bg-info/10',
        confirmationClassName: 'border-transparent bg-info text-info-foreground hover:bg-info/90'
      };
    case 'with_person':
      return {
        label: 'Leave with named person',
        helper: 'This student is approved to leave with a specific authorised person. Confirm the collecting person name when signing out.',
        icon: UsersIcon,
        badgeClassName: 'border-transparent bg-primary text-primary-foreground',
        cardClassName: 'border-primary/35 bg-primary/5 hover:border-primary/65 hover:shadow-primary/10',
        railClassName: 'bg-primary',
        iconClassName: 'bg-primary/15 text-primary',
        panelClassName: 'border-primary/25 bg-primary/10',
        confirmationClassName: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/90'
      };
    case 'with_teacher':
      return {
        label: 'Leave with teacher',
        helper: 'This student will travel back with a supervising teacher.',
        icon: GraduationCapIcon,
        badgeClassName: 'border-transparent bg-secondary text-secondary-foreground',
        cardClassName: 'border-secondary-foreground/25 bg-secondary/30 hover:border-secondary-foreground/50',
        railClassName: 'bg-secondary-foreground',
        iconClassName: 'bg-secondary/15 text-secondary-foreground',
        panelClassName: 'border-secondary-foreground/25 bg-secondary/10',
        confirmationClassName: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/90'
      };
    default:
      return {
        label: 'No parent response',
        helper: 'No verified SMS instruction is on file. Confirm the departure plan with staff.',
        icon: MessageSquareIcon,
        badgeClassName: 'border-border bg-muted text-muted-foreground',
        cardClassName: 'border-border bg-card hover:border-primary/35 hover:shadow-primary/10',
        railClassName: 'bg-muted-foreground',
        iconClassName: 'bg-muted text-muted-foreground',
        panelClassName: 'border-border bg-muted/65',
        confirmationClassName: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/90'
      };
  }
}

function preferenceBadge(preference: CheckoutType | null | undefined, authorizedPersonName?: string | null) {
  const presentation = getPreferencePresentation(preference);
  const Icon = presentation.icon;

  return (
    <Badge className={`gap-1 ${presentation.badgeClassName}`}>
      <Icon className="size-3" />
      {presentation.label}
      {preference === 'with_person' && authorizedPersonName ? `: ${authorizedPersonName}` : ''}
    </Badge>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

/** Format a "HH:MM" time-of-day string for display (e.g. "12:30 pm"). */
function formatTimeOfDay(time: string): string {
  const parts = time.split(':');
  const h = Number(parts[0] ?? 0);
  const m = Number(parts[1] ?? 0);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

/** Build the session time string for the header, or empty string if no times set. */
function formatSessionTimes(start: string | null, end: string | null): string {
  if (!start && !end) return '';
  if (start && end) return `${formatTimeOfDay(start)} – ${formatTimeOfDay(end)}`;
  if (start) return `from ${formatTimeOfDay(start)}`;
  return `until ${formatTimeOfDay(end!)}`;
}

/* ------------------------------ main component ----------------------------- */

export function EventWorkspacePage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: event } = useGetEvent(id);
  const { data: attendanceData, isLoading } = useGetEventAttendance(id);
  const attendance = attendanceData?.attendance ?? [];

  // SSE for real-time updates
  React.useEffect(() => {
    if (!id) return;
    const es = new EventSource(`/api/events/${id}/live`);
    es.onmessage = () => {
      queryClient.invalidateQueries({ queryKey: getGetEventAttendanceQueryKey(id) });
      queryClient.invalidateQueries({ queryKey: getListRollCallsQueryKey(id) });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          typeof query.queryKey[0] === 'string' &&
          (query.queryKey[0].includes('/sms-logs') ||
           query.queryKey[0].includes('/roll-calls/') ||
           query.queryKey[0].includes('/roll-call-entries')),
      });
      queryClient.invalidateQueries({ queryKey: ['/api/phone-approvals'] });
    };
    es.onerror = () => es.close();
    return () => es.close();
  }, [id, queryClient]);

  const [drawerStudentId, setDrawerStudentId] = React.useState<string | null>(null);
  const [signOutTarget, setSignOutTarget] = React.useState<AttendanceT | null>(null);

  const grouped = React.useMemo(() => {
    const map = new Map<string, AttendanceT[]>();
    for (const a of attendance) {
      const group = a.student?.group ?? 'Unassigned';
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(a);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [attendance]);

  const { data: rollCallsData } = useListRollCalls(id);
  const allRollCalls = rollCallsData?.rollCalls ?? [];
  const latestRollCall = allRollCalls.length > 0 ? allRollCalls[allRollCalls.length - 1] : null;
  const { data: latestEntriesData } = useGetRollCallEntries(latestRollCall?.id ?? '', {
    query: { enabled: !!latestRollCall?.id, queryKey: getGetRollCallEntriesQueryKey(latestRollCall?.id ?? '') }
  });
  const latestEntries = latestEntriesData?.entries ?? [];
  const latestEntryByStudent = new Map<string, RollCallEntry>(latestEntries.map((e) => [e.studentId, e]));

  const checkedIn = attendance.filter((a) => a.checkinStatus === 'checked_in').length;
  const signedOut = attendance.filter((a) => a.checkinStatus === 'signed_out').length;
  const rollPresent = latestEntries.filter((e) => e.status === 'present').length;
  const rollAbsent = latestEntries.filter((e) => e.status === 'absent').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title={event?.name ?? 'Loading…'}
        description={event ? [
          event.location,
          new Date(event.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }),
          formatSessionTimes(event.startTime, event.endTime)
        ].filter(Boolean).join(' · ') : ''}
        icon={CalendarDaysIcon}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild className="min-h-11 gap-2 md:min-h-9">
              <Link to={`/events/${id}/register`}>
                <SmartphoneIcon className="size-4" />
                Open mobile register
              </Link>
            </Button>
            {event?.status === 'active' ? (
              <>
                <Badge className="border-success/20 bg-success/10 text-success">
                  <span aria-hidden="true" className="size-1.5 animate-pulse rounded-full bg-success" />
                  Live updates
                </Badge>
                <CompleteEventButton eventId={id} />
              </>
            ) : (
              <Badge className="border-info/20 bg-info/10 text-info">Completed</Badge>
            )}
          </div>
        }
      />

      <Card className="flex flex-col gap-3 border-primary/25 bg-primary/5 p-4 shadow-sm sm:hidden">
        <div className="flex items-start gap-3">
          <span className="rounded-xl bg-primary/15 p-2.5 text-primary">
            <SmartphoneIcon aria-hidden className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold tracking-tight">Phone-friendly roll call</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Large swipe rows for present/absent and quick sign-out while you stand with the class.
            </p>
          </div>
        </div>
        <Button asChild className="min-h-12 w-full gap-2">
          <Link to={`/events/${id}/register`}>
            <SmartphoneIcon className="size-4" />
            Open mobile register
          </Link>
        </Button>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard icon={UsersIcon} label="Total students" value={attendance.length} tone="primary" />
        <StatCard icon={CheckIcon} label="Present" value={rollPresent} tone="success" />
        <StatCard icon={XIcon} label="Absent" value={rollAbsent} tone="warning" />
        <StatCard icon={ClockIcon} label="Awaiting checkout" value={checkedIn} tone="primary" />
        <StatCard icon={UserCheckIcon} label="Signed out" value={signedOut} tone="info" />
      </div>
      {latestRollCall && (
        <p className="text-xs font-medium text-muted-foreground">
          Headline roll call: <span className="text-foreground">{latestRollCall.name}</span> · {latestEntries.filter((e) => e.status !== 'pending').length}/{latestEntries.length} marked
        </p>
      )}

      {isLoading && <p className="py-10 text-center text-muted-foreground">Loading attendance…</p>}
      {!isLoading && attendance.length === 0 && (
        <Card className="items-center border-dashed border-primary/25 px-6 py-10 text-center shadow-sm">
          <span className="rounded-xl bg-primary/10 p-3 text-primary">
            <UsersIcon aria-hidden="true" className="size-5" />
          </span>
          <p className="font-semibold">No students checked in</p>
          <p className="text-sm text-muted-foreground">Attendance will appear here as students arrive for the event.</p>
        </Card>
      )}
      {grouped.map(([group, students]) => {
        const activeStudents = students.filter((student) => student.checkinStatus === 'checked_in').length;

        return (
          <section key={group} className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border/70 pb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Attendance group</p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight">{group}</h2>
              </div>
              <Badge className="border-success/20 bg-success/10 text-success">
                {activeStudents} awaiting checkout
              </Badge>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {students.map((a) => (
                <StudentCard
                  key={a.id}
                  attendance={a}
                  latestEntry={latestEntryByStudent.get(a.studentId)}
                  onSignOut={() => setSignOutTarget(a)}
                  onOpenDrawer={() => setDrawerStudentId(a.studentId)}
                />
              ))}
            </div>
          </section>
        );
      })}

      {/* Roll calls section */}
      <RollCallsSection eventId={id} />

      {/* Sign-out dialog */}
      <SignOutDialog
        target={signOutTarget}
        onClose={() => setSignOutTarget(null)}
        eventId={id}
      />

      {/* SMS evidence drawer */}
      <SmsEvidenceDrawer
        studentId={drawerStudentId}
        studentName={attendance.find((a) => a.studentId === drawerStudentId)?.student
          ? `${attendance.find((a) => a.studentId === drawerStudentId)?.student?.firstName} ${attendance.find((a) => a.studentId === drawerStudentId)?.student?.lastInitial}.`
          : undefined}
        onClose={() => setDrawerStudentId(null)}
      />
    </div>
  );
}

/* ------------------------------ roll calls section ------------------------------ */

function RollCallsSection({ eventId }: { eventId: string }) {
  const queryClient = useQueryClient();
  const { data: rollCallsData } = useListRollCalls(eventId);
  const rollCalls = rollCallsData?.rollCalls ?? [];

  const createMutation = useCreateRollCall();
  const deleteMutation = useDeleteRollCall();
  const [newName, setNewName] = React.useState('');
  const [renameTarget, setRenameTarget] = React.useState<RollCall | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<RollCall | null>(null);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    await createMutation.mutateAsync({ id: eventId, data: { name } });
    await queryClient.invalidateQueries({ queryKey: getListRollCallsQueryKey(eventId) });
    setNewName('');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync({ id: deleteTarget.id });
    await queryClient.invalidateQueries({ queryKey: getListRollCallsQueryKey(eventId) });
    setDeleteTarget(null);
  };

  return (
    <Card className="overflow-hidden border-border/80 py-0 shadow-sm">
      <div className="flex items-center justify-between border-b border-border/70 px-5 py-4">
        <div className="flex items-center gap-2">
          <ClipboardListIcon className="size-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Roll calls</h2>
        </div>
        <Badge className="border-border bg-muted text-muted-foreground">{rollCalls.length}</Badge>
      </div>

      {rollCalls.length === 0 && (
        <p className="px-5 py-8 text-center text-sm text-muted-foreground">No roll calls yet. Add one below.</p>
      )}

      <div className="divide-y divide-border/60">
        {rollCalls.map((rc: RollCall, idx: number) => (
          <RollCallRow key={rc.id} rollCall={rc} index={idx} eventId={eventId} onRename={() => setRenameTarget(rc)} onDelete={() => setDeleteTarget(rc)} />
        ))}
      </div>

      <div className="flex items-center gap-2 border-t border-border/70 px-5 py-3">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New roll call name…"
          className="flex-1"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          disabled={!newName.trim() || createMutation.isPending}
          onClick={handleAdd}
        >
          <PlusIcon className="size-3.5" />
          Add
        </Button>
      </div>

      {/* Rename dialog */}
      <RenameRollCallDialog
        target={renameTarget}
        onClose={() => setRenameTarget(null)}
        eventId={eventId}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="overflow-hidden">
          <DialogHeader>
            <DialogTitle>Delete roll call</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This will remove all marks recorded for this roll call. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              <TrashIcon className="mr-2 size-4" />
              {deleteMutation.isPending ? 'Deleting…' : 'Delete roll call'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function RollCallRow({
  rollCall,
  index,
  eventId,
  onRename,
  onDelete
}: {
  rollCall: RollCall;
  index: number;
  eventId: string;
  onRename: () => void;
  onDelete: () => void;
}) {
  const { data: entriesData } = useGetRollCallEntries(rollCall.id);
  const entries: RollCallEntry[] = entriesData?.entries ?? [];
  const present = entries.filter((e) => e.status === 'present').length;
  const absent = entries.filter((e) => e.status === 'absent').length;
  const pending = entries.filter((e) => e.status === 'pending').length;
  const hasMarks = present + absent > 0;

  return (
    <div className="flex items-center gap-3 px-5 py-3">
      <span className="w-6 shrink-0 text-center text-xs font-semibold text-muted-foreground tabular-nums">{index + 1}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{rollCall.name}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Badge className="border-success/20 bg-success/10 text-success">
            <CheckIcon className="size-3" />
            {present} present
          </Badge>
          <Badge className="border-destructive/20 bg-destructive/10 text-destructive">
            <XIcon className="size-3" />
            {absent} absent
          </Badge>
          <Badge variant="outline" className="text-muted-foreground">
            <ClockIcon className="size-3" />
            {pending} pending
          </Badge>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button asChild size="sm" variant="outline" className="gap-1">
          <Link to={`/events/${eventId}/register?rollCall=${rollCall.id}`}>
            <SmartphoneIcon className="size-3.5" />
            Register
          </Link>
        </Button>
        <Button size="icon" variant="ghost" className="size-8" onClick={onRename}>
          <PencilIcon className="size-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="size-8 text-destructive hover:text-destructive" onClick={onDelete}>
          <TrashIcon className="size-3.5" />
        </Button>
      </div>
      {hasMarks && (
        <span className="sr-only">This roll call has existing marks and requires confirmation to delete.</span>
      )}
    </div>
  );
}

function RenameRollCallDialog({
  target,
  onClose,
  eventId
}: {
  target: RollCall | null;
  onClose: () => void;
  eventId: string;
}) {
  const queryClient = useQueryClient();
  const mutation = useUpdateRollCall();
  const [name, setName] = React.useState('');

  React.useEffect(() => {
    setName(target?.name ?? '');
  }, [target]);

  const handleRename = async () => {
    if (!target || !name.trim()) return;
    await mutation.mutateAsync({ id: target.id, data: { name: name.trim() } });
    await queryClient.invalidateQueries({ queryKey: getListRollCallsQueryKey(eventId) });
    onClose();
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="overflow-hidden">
        <DialogHeader>
          <DialogTitle>Rename roll call</DialogTitle>
          <DialogDescription>Enter a new name for this roll call.</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Roll call name" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleRename} disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ stat card ------------------------------ */

function StatCard({
  icon: Icon,
  label,
  value,
  tone
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  tone: 'primary' | 'success' | 'warning' | 'info';
}) {
  const styles = {
    primary: 'border-primary/25 bg-primary/5 text-primary',
    success: 'border-success/25 bg-success/5 text-success',
    warning: 'border-warning/30 bg-warning/5 text-warning-foreground',
    info: 'border-info/25 bg-info/5 text-info'
  } as const;

  return (
    <Card className={`group relative gap-3 overflow-hidden border px-4 py-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${styles[tone]}`}>
      <span aria-hidden="true" className="absolute inset-x-0 top-0 h-0.5 bg-current opacity-70" />
      <div className="flex items-center justify-between gap-3">
        <span className="rounded-xl bg-card/75 p-2.5 shadow-xs ring-1 ring-current/10">
          <Icon aria-hidden="true" className="size-4" />
        </span>
        <p className="text-3xl font-bold tracking-tight tabular-nums text-foreground">{value}</p>
      </div>
      <p className="text-xs font-semibold tracking-wide text-muted-foreground">{label}</p>
    </Card>
  );
}

/* ------------------------------ student card ------------------------------ */

function StudentCard({
  attendance,
  latestEntry,
  onSignOut,
  onOpenDrawer
}: {
  attendance: AttendanceT;
  latestEntry?: RollCallEntry;
  onSignOut: () => void;
  onOpenDrawer: () => void;
}) {
  const signedOut = attendance.checkinStatus === 'signed_out';
  const student = attendance.student;
  const preference = signedOut ? attendance.checkoutType ?? attendance.smsPreference : attendance.smsPreference;
  const presentation = getPreferencePresentation(preference);
  const PreferenceIcon = presentation.icon;

  return (
    <Card
      className={`group relative gap-0 overflow-hidden border p-0 shadow-sm transition-all duration-200 ${
        signedOut
          ? 'border-info/25 bg-info/5 opacity-85'
          : latestEntry?.status === 'absent'
            ? `${presentation.cardClassName} opacity-75`
            : `${presentation.cardClassName} hover:-translate-y-0.5 hover:shadow-md`
      }`}
    >
      <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1 ${signedOut ? 'bg-info' : presentation.railClassName}`} />
      <div className="p-4 pl-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={onOpenDrawer}
              className="rounded-sm text-left text-base font-semibold tracking-tight transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              {student ? `${student.firstName} ${student.lastInitial}.` : 'Unknown student'}
            </button>
            <p className="mt-1 text-xs text-muted-foreground">Open parent SMS history</p>
          </div>
          {!signedOut && (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-primary/25 bg-card/80 text-primary hover:bg-primary/10"
              onClick={onSignOut}
            >
              <LogOutIcon className="size-3.5" />
              Sign out
            </Button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {signedOut ? (
            <Badge className="border-info/20 bg-info/10 text-info">
              <UserCheckIcon className="size-3" />
              Signed out
            </Badge>
          ) : (
            preferenceBadge(attendance.smsPreference, attendance.authorizedPersonName)
          )}
          {signedOut && preferenceBadge(preference, attendance.authorizedPersonName)}
          {latestEntry?.status === 'present' && (
            <Badge className="border-success/20 bg-success/10 text-success">
              <CheckIcon className="size-3" />
              Present
            </Badge>
          )}
          {latestEntry?.status === 'absent' && (
            <Badge className="border-destructive/20 bg-destructive/10 text-destructive">
              <XIcon className="size-3" />
              Absent
            </Badge>
          )}
          {(!latestEntry || latestEntry.status === 'pending') && !signedOut && (
            <Badge variant="outline" className="text-muted-foreground">
              Not marked
            </Badge>
          )}
          {student?.guardians?.length === 0 && !signedOut && (
            <Badge className="border-destructive/20 bg-destructive/10 text-destructive">
              <AlertTriangleIcon className="size-3" />
              No guardian
            </Badge>
          )}
        </div>

        {!signedOut && (
          <div className={`mt-3 flex items-start gap-3 rounded-xl border p-3 ${presentation.panelClassName}`}>
            <span className={`rounded-lg p-2 ${presentation.iconClassName}`}>
              <PreferenceIcon aria-hidden="true" className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold">{presentation.label}</p>
              <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{presentation.helper}</p>
            </div>
          </div>
        )}

        {signedOut && attendance.signedOutBy && (
          <p className="mt-3 border-t border-info/15 pt-3 text-xs text-muted-foreground">
            Checked out by {attendance.signedOutBy} · {formatTime(attendance.lastUpdatedAt)}
          </p>
        )}
        {!signedOut && latestEntry && latestEntry.status !== 'pending' && latestEntry.markedBy && (
          <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
            Roll call by {latestEntry.markedBy} · {formatTime(latestEntry.markedAt ?? attendance.lastUpdatedAt)}
          </p>
        )}
      </div>
    </Card>
  );
}

/* ------------------------------ sign-out dialog ------------------------------ */

function SignOutDialog({
  target,
  onClose,
  eventId: _eventId
}: {
  target: AttendanceT | null;
  onClose: () => void;
  eventId: string;
}) {
  const queryClient = useQueryClient();
  const mutation = useUpdateAttendance();
  const [teacherName, setTeacherName] = React.useState('');
  const [checkoutType, setCheckoutType] = React.useState<CheckoutType>('by_self');
  const [personName, setPersonName] = React.useState('');

  React.useEffect(() => {
    if (target?.smsPreference) {
      setCheckoutType(target.smsPreference);
    } else {
      setCheckoutType('by_self');
    }
    setTeacherName('');
    setPersonName(target?.authorizedPersonName ?? '');
  }, [target]);

  if (!target) return null;

  const student = target.student;
  const parentInstruction = getPreferencePresentation(target.smsPreference);
  const selectedCheckout = getPreferencePresentation(checkoutType);
  const ParentInstructionIcon = parentInstruction.icon;
  const SelectedCheckoutIcon = selectedCheckout.icon;

  const handleSignOut = async () => {
    await mutation.mutateAsync({
      id: target.id,
      data: {
        checkinStatus: 'signed_out',
        checkoutType,
        signedOutBy: teacherName || 'Teacher',
        ...(checkoutType === 'with_person' ? { authorizedPersonName: personName.trim() || null } : {})
      }
    });
    await queryClient.invalidateQueries({ queryKey: getGetEventAttendanceQueryKey(_eventId) });
    onClose();
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="overflow-hidden">
        <DialogHeader className="border-b border-primary/10 pb-4 pr-8">
          <DialogTitle>Sign out {student?.firstName} {student?.lastInitial}.</DialogTitle>
          <DialogDescription>
            Review the verified parent instruction, then record the departure plan for the audit trail.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className={`flex items-start gap-3 rounded-xl border p-4 ${parentInstruction.panelClassName}`}>
            <span className={`rounded-lg p-2 ${parentInstruction.iconClassName}`}>
              <ParentInstructionIcon aria-hidden="true" className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold">Parent instruction</p>
                {preferenceBadge(target.smsPreference, target.authorizedPersonName)}
              </div>
              <p className="mt-1 text-sm leading-5 text-muted-foreground">{parentInstruction.helper}</p>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="so-type">Confirm checkout plan</Label>
            <Select value={checkoutType} onValueChange={(value) => setCheckoutType(value as CheckoutType)}>
              <SelectTrigger id="so-type" className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="by_self">Go home independently</SelectItem>
                <SelectItem value="wait_for_parent">Wait for parent pickup</SelectItem>
                <SelectItem value="back_to_school">Return to school</SelectItem>
                <SelectItem value="with_person">Leave with named person</SelectItem>
                <SelectItem value="with_teacher">Leave with teacher</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${selectedCheckout.panelClassName}`}>
            <span className={`rounded-lg p-1.5 ${selectedCheckout.iconClassName}`}>
              <SelectedCheckoutIcon aria-hidden="true" className="size-3.5" />
            </span>
            <p className="text-sm font-medium">Checkout will be recorded as: {selectedCheckout.label}</p>
          </div>
          {checkoutType === 'with_person' && (
            <div className="grid gap-2">
              <Label htmlFor="so-person-name">Collecting person name</Label>
              <Input
                id="so-person-name"
                placeholder="e.g. Grandma Sue"
                value={personName}
                onChange={(event) => setPersonName(event.target.value)}
              />
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="so-teacher">Teacher name for the audit record</Label>
            <Input
              id="so-teacher"
              placeholder="Your name"
              value={teacherName}
              onChange={(event) => setTeacherName(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className={selectedCheckout.confirmationClassName} onClick={handleSignOut} disabled={mutation.isPending || (checkoutType === 'with_person' && !personName.trim())}>
            <LogOutIcon className="size-4" />
            {mutation.isPending ? 'Saving…' : 'Confirm sign out'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ SMS evidence drawer ------------------------------ */

function SmsEvidenceDrawer({
  studentId,
  studentName,
  onClose
}: {
  studentId: string | null;
  studentName?: string;
  onClose: () => void;
}) {
  const { session } = useSession();
  const isAdmin = session.status === 'signedIn' && session.user.role === 'admin';
  const { data, isLoading } = useGetStudentSmsLogs(studentId!);
  const logs = data?.logs ?? [];

  return (
    <Sheet open={!!studentId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="overflow-y-auto border-l border-info/20 bg-background p-6 sm:max-w-md">
        <SheetHeader className="border-b border-info/15 pb-5 pr-8">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-info/10 p-2.5 text-info">
              <MessageSquareIcon aria-hidden="true" className="size-4" />
            </span>
            <div>
              <SheetTitle>Parent SMS timeline</SheetTitle>
              <SheetDescription className="mt-1">
                {studentName ?? 'Student'} · verified incoming instructions
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-5">
          {isLoading && (
            <div className="space-y-3 border-l border-info/20 pl-5">
              <Card className="h-28 animate-pulse border-info/15 bg-info/5 p-0" />
              <Card className="h-28 animate-pulse border-info/15 bg-info/5 p-0" />
            </div>
          )}
          {!isLoading && logs.length === 0 && (
            <Card className="items-center border-dashed border-info/25 bg-info/5 px-5 py-9 text-center shadow-none">
              <span className="rounded-xl bg-info/10 p-3 text-info">
                <MessageSquareIcon aria-hidden="true" className="size-5" />
              </span>
              <p className="font-semibold">No parent messages yet</p>
              <p className="text-sm text-muted-foreground">Incoming SMS instructions will appear here in real time.</p>
            </Card>
          )}
          {!isLoading && logs.length > 0 && (
            <div className="space-y-4 border-l border-info/25 pl-5">
              {logs.map((log: SmsLog) => {
                const presentation = getPreferencePresentation(log.parsedPreference);
                const PreferenceIcon = presentation.icon;

                return (
                  <div key={log.id} className="relative">
                    <span aria-hidden="true" className="absolute -left-7 top-4 flex size-4 items-center justify-center rounded-full border-4 border-background bg-info" />
                    <Card className="gap-0 border-info/20 bg-info/5 p-0 shadow-sm shadow-info/5 transition-shadow hover:shadow-md">
                      <div className="space-y-3 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="font-mono text-xs font-medium text-muted-foreground">{log.senderPhone}</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {new Date(log.receivedAt).toLocaleString('en-AU')}
                            </p>
                          </div>
                          <div className="flex flex-wrap justify-end gap-1.5">
                            {preferenceBadge(log.parsedPreference, log.authorizedPersonName)}
                            {log.isValidated ? (
                              <Badge className="border-success/20 bg-success/10 text-success">
                                <CheckIcon className="size-3" />
                                Verified sender
                              </Badge>
                            ) : (
                              <>
                                <Badge className="border-destructive/20 bg-destructive/10 text-destructive">
                                  <AlertTriangleIcon className="size-3" />
                                  Unknown number — needs approval
                                </Badge>
                                {isAdmin && (
                                  <Button asChild size="sm" variant="outline" className="h-6 gap-1 px-2 text-xs">
                                    <Link to="/approved-numbers">Approve number</Link>
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <p className="rounded-xl bg-card/85 px-3 py-2.5 text-sm leading-6 shadow-xs">“{log.messageBody}”</p>
                        <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium ${presentation.panelClassName}`}>
                          <PreferenceIcon aria-hidden="true" className="size-3.5 shrink-0" />
                          Parsed as {presentation.label}
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------ complete event button ------------------------------ */

function CompleteEventButton({ eventId }: { eventId: string }) {
  const queryClient = useQueryClient();
  const mutation = useUpdateEvent();

  const handleComplete = async () => {
    await mutation.mutateAsync({
      id: eventId,
      data: { status: 'completed' }
    });
    await queryClient.invalidateQueries({ queryKey: getGetEventAttendanceQueryKey(eventId) });
  };

  return (
    <Button variant="outline" onClick={handleComplete} disabled={mutation.isPending}>
      <CheckIcon className="mr-2 size-4" />
      {mutation.isPending ? 'Completing…' : 'Complete Event'}
    </Button>
  );
}
